/**
 * Student Slide View
 * Wrapper component for student's view of slides — Nearpod-style immersive experience
 */

import { useEffect, useRef, useState } from 'react';
import { Clock } from 'lucide-react';
import { motion } from 'motion/react';
import type { Slide } from '../../../utils/supabase/client';
import { ContentSlideRenderer, ActivitySlideRenderer } from './index';
import { isContentSlideType, activityRendersOwnTitle } from '../../../utils/slides/slideRegistry';
import { stripHtml } from '../../../utils/slides/stripHtml';

const DEFAULT_ACTIVITY_TIME_LIMIT_SECONDS = 60;

interface StudentSlideViewProps {
  slide: Slide;
  onSubmit: (answer: any) => void;
  onAnswerChange: (answer: any) => void;
  selectedAnswer: any;
  hasSubmitted: boolean;
  shuffleSeed?: string;
  sessionMode?: 'live' | 'student-paced';
  className?: string;
}

export default function StudentSlideView({ 
  slide,
  onSubmit,
  onAnswerChange,
  selectedAnswer,
  hasSubmitted,
  shuffleSeed,
  sessionMode = 'live',
  className = ''
}: StudentSlideViewProps) {
  const isContentSlide = isContentSlideType(slide.type);
  const hasBuiltInTimer = slide.type === 'quiz' || slide.type === 'time_to_climb';
  const hasAnswerTimer = !isContentSlide && !hasBuiltInTimer && slide.type !== 'collaborate_board';
  const configuredTimeLimit = Number(slide.content?.timeLimit);
  const timeLimit = Number.isInteger(configuredTimeLimit) && configuredTimeLimit > 0
    ? configuredTimeLimit
    : DEFAULT_ACTIVITY_TIME_LIMIT_SECONDS;
  const [timeLeft, setTimeLeft] = useState(timeLimit);
  const timeoutSubmittedRef = useRef(false);

  useEffect(() => {
    setTimeLeft(timeLimit);
    timeoutSubmittedRef.current = false;
  }, [slide.id, timeLimit]);

  useEffect(() => {
    if (!hasAnswerTimer || hasSubmitted || timeLeft <= 0) return;

    const timer = setTimeout(() => setTimeLeft((remaining) => Math.max(remaining - 1, 0)), 1000);
    return () => clearTimeout(timer);
  }, [hasAnswerTimer, hasSubmitted, timeLeft]);

  useEffect(() => {
    if (!hasAnswerTimer || hasSubmitted || timeLeft !== 0 || timeoutSubmittedRef.current) return;

    timeoutSubmittedRef.current = true;
    onSubmit(selectedAnswer ?? {});
  }, [hasAnswerTimer, hasSubmitted, onSubmit, selectedAnswer, timeLeft]);

  const formattedTimeLeft = `${Math.floor(timeLeft / 60)}:${(timeLeft % 60).toString().padStart(2, '0')}`;

  return (
    <div className={`${className}`}>
      {/* Slide Title — activity slides that don't render their own title */}
      {!isContentSlide && !activityRendersOwnTitle(slide.type) && (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3 }}
          className="text-center mb-6 sm:mb-8"
        >
          {slide.content?.showTitle !== false && (
            <h2 className="text-2xl sm:text-3xl font-extrabold mb-3 sm:mb-4 tracking-tight leading-tight">
              {slide.content?.title || 'Activity'}
            </h2>
          )}
          {slide.content?.question && (
            <div className="text-base sm:text-xl text-muted-foreground max-w-2xl mx-auto leading-relaxed">
              {stripHtml(slide.content.question)}
            </div>
          )}
        </motion.div>
      )}

      {/* Render the slide */}
      {isContentSlide ? (
        <ContentSlideRenderer 
          slide={slide} 
          viewMode="student" 
        />
      ) : (
        <>
          {hasAnswerTimer && (
            <div className="mb-4 flex justify-end">
              <div
                role="timer"
                aria-label={`Time remaining ${formattedTimeLeft}`}
                className={`inline-flex items-center gap-2 rounded-full px-4 py-1.5 font-mono text-sm ${
                  timeLeft <= 10 ? 'bg-destructive/15 text-destructive animate-pulse' : 'bg-muted/60 text-muted-foreground'
                }`}
              >
                <Clock className="h-4 w-4" />
                <span className="font-bold">{formattedTimeLeft}</span>
              </div>
            </div>
          )}
          <ActivitySlideRenderer
            slide={slide}
            onSubmit={onSubmit}
            onAnswerChange={onAnswerChange}
            selectedAnswer={selectedAnswer}
            hasSubmitted={hasSubmitted}
            shuffleSeed={shuffleSeed}
            sessionMode={sessionMode}
            viewMode="student"
          />
        </>
      )}
    </div>
  );
}
